import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { Card, Grid } from "@mui/material";
import { connect } from "react-redux";
import Swal from "sweetalert2";
import MessageTypes from "enums/message-types";

import { useHistory } from "react-router-dom";
import validations from "layouts/user/components/user-details/schemas/userFormValidations";
import initialForm from "layouts/user/components/user-details/schemas/userForm";
import localizeForm from "layouts/user/components/user-details/schemas/userLocalizeForm";
import initialValues from "layouts/user/components/user-details/schemas/userFormInitialValues";
import { removeMessage } from "redux/actions/message";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";

import { getSubOrganizationListByOrganizationId } from "redux/actions/pages/sub-organization";
import { startNewUserScreen, saveUser } from "redux/actions/pages/user";
import { startAllClassesScreen } from "redux/actions/pages/class";
import UserForm from "../UserForm";

const UserDetails = ({
  localizations,
  message,
  userData,
  allOrganizations,
  removeErrorMsg,
  startScreen,
  saveNewUser,
  subOrganizations,
  classes,
  fetchSubOrganizations,
  fetchClasses,
}) => {
  const [form, setForm] = useState(initialForm);
  const history = useHistory();

  const isEditMode = userData && userData?.id > 0;

  useEffect(() => {
    startScreen();
  }, []);

  useEffect(() => {
    if (localizations) {
      setForm(localizeForm(form, localizations));
    }
  }, [localizations]);

  useEffect(() => {
    if (isEditMode && userData?.organizationId) {
      fetchSubOrganizations({
        organizationId: userData.organizationId,
      });
      if (isEditMode && userData?.subOrganizationId) {
        fetchClasses({
          organizationId: userData.organizationId,
          subOrganizationId: userData.subOrganizationId,
        });
      }
    }
  }, [userData]);

  useEffect(() => {
    if (
      message &&
      message.messageContent !== undefined &&
      message.messageContent !== null &&
      message.messageContent !== ""
    ) {
      Swal.fire({
        icon: message.messageType || "error",
        title:
          message.messageTitle === MessageTypes.success
            ? localizations?.MESSAGE_SUCCESS
            : localizations?.MESSAGE_ERROR,
        text: message.messageContent,
        showCancelButton: false,
        confirmButtonText: localizations?.MESSAGE_GO_HOME_CONFIRM_BTN,
      }).then(() => {
        removeErrorMsg();
        history.push("/user");
      });
    }
  }, [message]);

  const handleFetchRequest = (type, orgId, subOrgId) => {
    if (type === "ORG_CHANGED" && orgId) {
      fetchSubOrganizations({ organizationId: orgId });
    }

    if (type === "SUB_ORG_CHANGED" && orgId && subOrgId) {
      fetchClasses({ organizationId: orgId, subOrganizationId: subOrgId });
    }
  };

  const getInitialValues = () => {
    if (userData && userData?.id > 0) {
      let selectedOrganization = null;
      if (userData.organizationId && Array.isArray(allOrganizations)) {
        const foundOrganization = allOrganizations.find(
          (organization) => organization.id === userData.organizationId
        );
        if (foundOrganization) {
          selectedOrganization = {
            value: foundOrganization.id,
            label: foundOrganization.organizationKey,
          };
        }
      }
      let selectedSubOrganization = null;
      if (userData.subOrganizationId && Array.isArray(subOrganizations)) {
        const foundSubOrganization = subOrganizations.find(
          (subOrganization) => subOrganization.id === userData.subOrganizationId
        );
        if (foundSubOrganization) {
          selectedSubOrganization = {
            value: foundSubOrganization.id,
            label: foundSubOrganization.subOrganizationKey,
          };
        }
      }

      let selectedClass = null;
      if (Array.isArray(classes)) {
        if (userData.userType === "OTHER" && userData.classIdList) {
          const classIdList = userData.classIdList
            .toString()
            .split(",")
            .map((id) => Number(id.trim()));
          const foundClass = classes.filter((c) => classIdList.includes(c.id));
          if (foundClass.length > 0) {
            selectedClass = foundClass.map((c) => ({
              value: c.id,
              label: c.className,
            }));
          } else {
            selectedClass = [];
          }
        } else {
          const foundClass = classes.find((c) => c.id === Number(userData.classIdList.trim()));
          if (foundClass) {
            selectedClass = {
              value: foundClass.id,
              label: foundClass.className,
            };
          }
        }
      }

      let selectedUserType = null;
      if (userData.userType) {
        if (userData.userType === "STUDENT")
          selectedUserType = { value: "STUDENT", label: "ÖĞRENCİ" };
        else if (userData.userType === "TEACHER")
          selectedUserType = { value: "TEACHER", label: "ÖĞRETMEN" };
        else selectedUserType = { value: userData.userType, label: userData.userType };
      }

      let filterGardeList = null;
      if (userData.gradeList) {
        filterGardeList = userData.gradeList
          .split(",")
          .map((item) => item.replace("GRADE_", "").trim())
          .join(", ");
      }

      return {
        organization: selectedOrganization,
        subOrganization: selectedSubOrganization,
        classData: selectedClass,
        email: userData.email || "",
        userType: selectedUserType,
        name: userData.name || "",
        surname: userData.surname || "",
        nickName: userData.nickName || "",
        studentNo: userData.studentNo || "",
        grade: filterGardeList || "",
        allBooksEnabled: userData.allBooksEnabled || false,
      };
    }
    return initialValues;
  };

  const handleSubmit = (values, actions) => {
    const formSubOrgValue = values.subOrganization;
    const safeSubOrgId = formSubOrgValue?.value ?? formSubOrgValue ?? null;
    const oldClassIdList = isEditMode ? userData.classIdList : null;

    const baseUserObject = {
      organizationId: values.organization?.value ?? null,
      organizationKey: values.organization?.label ?? "",
      subOrganizationId: isEditMode ? userData.subOrganizationId : safeSubOrgId,
      subOrganizationKey: values.subOrganization?.label ?? "",
      email: values.email,
      userType: values.userType?.label ?? null,
      name: values.name,
      surname: values.surname,
      nickName: values.nickName,
      studentNo: values.studentNo === "" ? null : values.studentNo,
      allBooksEnabled: values.allBooksEnabled,
      isClassOwner: values.isClassOwner,
      currentClassId: oldClassIdList,

      ...(isEditMode && { id: userData.id }),
      ...(isEditMode && { userId: userData.id }),
    };

    let userObjectsArray = [];
    const classVal = values.classData;

    const extractGrade = (label) => {
      if (!label) return "";
      return label.toString().charAt(0);
    };

    if (isEditMode) {
      let combinedClassIdList = null;
      let combinedGradeList = null;

      if (Array.isArray(classVal) && classVal.length > 0) {
        combinedClassIdList = classVal.map((c) => c.value).join(",");
        combinedGradeList = classVal.map((c) => extractGrade(c.label)).join(",");
      } else if (classVal && classVal.value) {
        combinedClassIdList = classVal.value.toString();
        combinedGradeList = extractGrade(classVal.label);
      }

      userObjectsArray = [
        {
          ...baseUserObject,
          className: "",
          classId: combinedClassIdList,
          grade: combinedGradeList,
        },
      ];
    } else if (classVal && Array.isArray(classVal) && classVal.length > 0) {
      userObjectsArray = classVal.map((cls) => ({
        ...baseUserObject,
        className: cls.label,
        classId: cls.value.toString(),
        grade: extractGrade(cls.label),
      }));
    } else if (classVal && classVal.value) {
      userObjectsArray = [
        {
          ...baseUserObject,
          className: classVal.label,
          classId: classVal.value.toString(),
          grade: extractGrade(classVal.label),
        },
      ];
    } else {
      userObjectsArray = [
        {
          ...baseUserObject,
          className: "",
          classId: null,
          grade: null,
        },
      ];
    }

    const payload = {
      userData: userObjectsArray,
    };

    saveNewUser(payload);

    actions.setSubmitting(false);
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={validations.validation}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form id={form.formId} autoComplete="off">
            <Grid container spacing={3}>
              <Grid item xs={12} lg={12}>
                <Card sx={{ overflow: "visible" }}>
                  <SuiBox p={3}>
                    <UserForm
                      formData={{
                        formField: form.formField,
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        setFieldValue,
                        setFieldTouched,
                      }}
                      allOrganizations={allOrganizations}
                      subOrganizations={subOrganizations}
                      classes={classes}
                      onFetchRequest={handleFetchRequest}
                      isEditMode={isEditMode}
                    />
                    <SuiBox display="flex" my={5}>
                      <SuiBox ml="auto">
                        <SuiButton
                          disabled={isSubmitting}
                          type="submit"
                          variant="contained"
                          color="dark"
                          size="small"
                        >
                          {localizations?.FORM_SAVE_BTN}
                        </SuiButton>
                      </SuiBox>
                    </SuiBox>
                  </SuiBox>
                </Card>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

UserDetails.defaultProps = {
  localizations: null,
  message: null,
  allOrganizations: null,
  subOrganizations: null,
  classes: null,
  userData: null,
  fetchSubOrganizations: () => {},
  fetchClasses: () => {},
};

UserDetails.propTypes = {
  localizations: PropTypes.objectOf(PropTypes.any),
  message: PropTypes.objectOf(PropTypes.any),
  removeErrorMsg: PropTypes.func.isRequired,
  startScreen: PropTypes.func.isRequired,
  saveNewUser: PropTypes.func.isRequired,
  userData: PropTypes.objectOf(PropTypes.any),
  classes: PropTypes.arrayOf(PropTypes.any),
  allOrganizations: PropTypes.arrayOf(PropTypes.any),
  subOrganizations: PropTypes.arrayOf(PropTypes.any),
  fetchSubOrganizations: PropTypes.func,
  fetchClasses: PropTypes.func,
};

const mapStateToProps = (state) => ({
  localizations: state.localization?.pairs,
  allOrganizations: state.data?.organizations ?? [],
  subOrganizations: state.data?.subOrganizations ?? [],
  classes: state.data?.classes ?? [],
});

const mapDispatchToProps = {
  removeErrorMsg: removeMessage,
  startScreen: startNewUserScreen,
  saveNewUser: saveUser,
  fetchSubOrganizations: getSubOrganizationListByOrganizationId,
  fetchClasses: startAllClassesScreen,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserDetails);
